/**
 * Plan settings — what an organisation's plan sets for its people: the agents
 * and their models, and the product's capabilities.
 *
 * One schema serves three parties that must not disagree: the gateway
 * validates plan documents and lays an organisation's plan over the default
 * plan, the server lays the result over the settings the release ships, and
 * the client gates its surfaces on the features. The release file
 * (`operator-defaults.json` in `@dovod/config`) is the only complete source,
 * so an install with no gateway, no credential to evaluate with, or no answer
 * yet runs on it — the restricted posture, every feature off.
 */
import * as Result from "effect/Result";
import * as Schema from "effect/Schema";
import * as Struct from "effect/Struct";
import { TrimmedNonEmptyString } from "./baseSchemas.js";
/** The agent a turn runs on. A plan names it; the second provider adds its section beside `codex`. */
export const AgentKind = Schema.Literals(["codex"]);
/**
 * A model selection. The instance is always the shipped one of its agent, so
 * the value names only what an operator chooses. `reasoningEffort` is
 * open-ended because the Codex protocol is: each model advertises the efforts
 * it accepts.
 */
export const AgentModelSetting = Schema.Struct({
    model: TrimmedNonEmptyString,
    reasoningEffort: Schema.optionalKey(TrimmedNonEmptyString),
    fastMode: Schema.optionalKey(Schema.Boolean),
});
/** `turn` runs the user's turns; `service` runs auxiliary calls such as chat titles. */
export const CodexAgentSettings = Schema.Struct({
    turn: AgentModelSetting,
    service: AgentModelSetting,
});
/** The capabilities a client sees and gates its surfaces on. */
export const PlanFeatures = Schema.Struct({
    deepWork: Schema.Boolean,
    voiceInput: Schema.Boolean,
});
/** A full set of settings. Every key is required: this is what is in force. */
export const PlanSettings = Schema.Struct({
    agents: Schema.Struct({
        default: AgentKind,
        codex: CodexAgentSettings,
    }),
    features: PlanFeatures,
});
/**
 * What a plan document, the gateway's answer, and the server's dev override
 * hold: any key at any level may be absent. A model selection is whole when
 * present.
 */
export const PlanSettingsPatch = Schema.Struct({
    agents: Schema.optionalKey(Schema.Struct({
        default: Schema.optionalKey(AgentKind),
        codex: Schema.optionalKey(CodexAgentSettings.mapFields(Struct.map(Schema.optionalKey))),
    })),
    features: Schema.optionalKey(PlanFeatures.mapFields(Struct.map(Schema.optionalKey))),
});
/**
 * Where the settings in force came from. Diagnostic only: `defaults` covers
 * both "no gateway configured" and "the gateway failed", because a lawyer must
 * not be able to tell those apart — the server's own log distinguishes them.
 */
export const SettingsSource = Schema.Literals(["defaults", "remote", "override"]);
/**
 * Paths a higher layer replaces whole. A reasoning effort belongs to its
 * model: merged key by key, a plan that names another model would inherit an
 * effort that model may reject, and its first turn would fail.
 */
const WHOLE_PATHS = new Set(["agents.codex.turn", "agents.codex.service"]);
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function mergeAt(path, base, patch) {
    if (WHOLE_PATHS.has(path) || !isRecord(base) || !isRecord(patch))
        return patch;
    const merged = { ...base };
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            continue;
        merged[key] = mergeAt(path === "" ? key : `${path}.${key}`, base[key], value);
    }
    return merged;
}
/**
 * Lay a patch over a full set of settings or over another patch. Objects merge
 * key by key; a model selection replaces the base's whole.
 */
export function mergePlanSettings(base, patch) {
    return mergeAt("", base, patch);
}
const decodePatch = Schema.decodeUnknownResult(PlanSettingsPatch);
/**
 * Decode a plan document. `strict` refuses unknown keys: it is for operator
 * input, where an unknown key is a typo that would otherwise save as a
 * setting nothing enforces. Every reader of a stored or received document
 * decodes leniently and drops unknown keys, so desktop and backend releases of
 * different ages read each other's documents.
 */
export function decodePlanSettingsPatch(input, options) {
    const decoded = decodePatch(input, {
        onExcessProperty: options.strict ? "error" : "ignore",
    });
    return Result.isSuccess(decoded)
        ? { ok: true, settings: decoded.success }
        : { ok: false, message: decoded.failure.message };
}
